
import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action, State } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import { Case, User } from "@/types"
import SkeletonLoader from "../SkeletonLoader.vue"
import vSelect from "vue-select"
import UserImage from "@/components/UserImage.vue"

import api from "@/services/api"
import { UserStatus } from "@/types/userStatus"
import apiUserStatus from "@/services/api_UserStatus"
import { CurrentUser } from "@/types/user"

import _ from "lodash"

const caseStore = namespace("cases")
const setup = namespace("setup")

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"skeleton": SkeletonLoader,
		"v-select": vSelect,
		"user-image": UserImage
	}
})
export default class TeamView extends Vue {
	@Prop()
	value: any;

	@Prop()
	edit!: boolean

	@Getter("getMe") public me!: CurrentUser;

	@Getter("getAvailableUserStatuses") public availableUserStatuses!: UserStatus[];

	@caseStore.Action("detailsLoad")
	private detailsLoad!: () => void;

	@Action("loadAvailableUserStatuses")
	private loadAvailableUserStatuses!: () => void;

	@Action("setCurrentUserStatus")
	private setCurrentUserStatus!: (userStatus: UserStatus) => void;

	@Action("clearCurrentUserStatus")
	private clearCurrentUserStatus!: () => void;

	@State
	teams!: any[];

	activeTeam: any = null;
	loading: boolean = true;
	users: User[] = [];

	public async mounted () {
		this.onEditChanged()
		await this.loadAvailableUserStatuses()
	}

	private gotoAccount (user: User) {
		this.$router.push({ path: `/userprofile/${user.id}` })
	}

	@Watch("edit")
	private async onEditChanged () {
		if (!this.edit) {
			this.loading = true

			if (this.value && this.value.teamId) {
				if (!this.activeTeam || this.activeTeam.id !== this.value.teamId) {
					if (this.teams) {
						this.activeTeam = this.teams.find(v => v.id === this.value.teamId)
					}
				}

				this.users = (await api.usersGet(this.value.teamId)).filter(user => !user.disabled)
			}

			this.loading = false
		}
	}

	@Emit("input")
	private teamChanged (teamId: string) {
		return { ...this.value, teamId }
	}

	private async userStatusChanged (userId: string, userStatus: UserStatus) {
		if (userId === this.me.id) {
			if (userStatus) {
				await this.setCurrentUserStatus(userStatus)
				return
			}
			await this.clearCurrentUserStatus()
			return
		}

		if (userStatus) {
			await api.setUserStatus(userId, userStatus.id!)
		} else {
			await api.clearUserStatus(userId)
		}
	}

	public get userHasSetStatusPermission () : boolean {
		return !!(this.me.extendedPermissions &&
			this.me.extendedPermissions["setOtherUsersStatus"])
	}

	public get usersWithDisplayOptions () : UserWithDisplayOptions[] {
		if (!this.value) {
			return []
		}

		const userStatusIdsToFilter = this.value.userStatuses
			? this.value.userStatuses.map((c: UserStatus) => c.id)
			: []

		const allUsers: UserWithDisplayOptions[] = []

		for (let user of this.users) {
			const hasMatchingStatus =
				userStatusIdsToFilter.length === 0 ||
				(user.currentStatus && userStatusIdsToFilter.includes(user.currentStatus.id))

			const isInHighlightedTeam = this.userIsInHighlightedTeams(user)

			allUsers.push({
				user: user,
				hidden: !(hasMatchingStatus || isInHighlightedTeam) && !this.value.showHiddenAsMuted,
				muted: !hasMatchingStatus && this.value.showHiddenAsMuted,
				isInHighlightedTeam: isInHighlightedTeam
			})
		}

		const sortedUsers = _.orderBy(allUsers, ["isInHighlightedTeam", "muted"], ["desc", "asc"])
		return sortedUsers
	}

	private userIsInHighlightedTeams (user: User) : boolean {
		if (!this.value ||
			!this.value.highlightedTeamIds ||
			this.value.highlightedTeamIds.length === 0 ||
			!user.teams) {
			return false
		}

		const userTeamIds = user.teams.map(c => c.id) as number[]
		const highlightedTeamIds = this.value.highlightedTeamIds as number[]

		return userTeamIds.some(userTeamId => highlightedTeamIds.includes(userTeamId))
	}
}

interface UserWithDisplayOptions {
	user: User,
	hidden: boolean,
	muted: boolean,
	isInHighlightedTeam: boolean
}
